import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function RouteUtilizationChart({ data: chartData = [] }) {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Use props data if available, fallback to mock data
  const defaultData = [
    { route: 'Delhi to Noida', utilization: 65, capacity: 100 },
    { route: 'Noida to Faridabad', utilization: 80, capacity: 100 },
    { route: 'Faridabad to Gurgaon', utilization: 45, capacity: 100 },
    { route: 'Gurgaon to Delhi', utilization: 90, capacity: 100 },
    { route: 'Delhi to Gurgaon', utilization: 72, capacity: 100 },
    { route: 'Noida to Delhi', utilization: 58, capacity: 100 }
  ];

  const dataToUse = chartData.length > 0 ? chartData : defaultData;
  const utilizationData = dataToUse.map(item => item.utilization);

  const data = {
    labels: dataToUse.map(item => item.route),
    datasets: [
      {
        label: 'Utilization %',  
        data: utilizationData,
        backgroundColor: utilizationData.map(util => {
          if (util < 30) return 'rgba(255, 193, 7, 0.8)';      // Yellow for under-utilized
          return 'rgba(220, 53, 69, 0.8)';                     // Red for over-utilized
        }),
        borderColor: utilizationData.map(util => {
          if (util < 30) return 'rgba(255, 193, 7, 1)';        // Yellow for under-utilized
          return 'rgba(220, 53, 69, 1)';                       // Red for over-utilized
        }),
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 14,
            weight: 'bold',
          },
        },
      },
      title: {
        display: true,
        text: 'Bus Seat Utilization per Route',
        font: {
          size: 18,
          weight: 'bold',
        },
        color: '#333',
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#ddd',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            return `${context.parsed.y}% utilization`;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: 'Utilization %',
          font: {
            size: 14,
            weight: 'bold',
          },
          color: '#333',
        },
        ticks: {
          font: {
            size: windowWidth < 768 ? 10 : 12,
          },
          color: '#666',
          callback: function(value) {
            return value + '%';
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
      },
      x: {
        title: {
          display: true,
          text: 'Routes',
          font: {
            size: 14,
            weight: 'bold',
          },
          color: '#333',
        },
        ticks: {
          font: {
            size: windowWidth < 768 ? 9 : 11,
          },
          color: '#666',
          maxRotation: windowWidth < 480 ? 90 : windowWidth < 768 ? 60 : 45,
          minRotation: 0,
        },
        grid: {
          display: false,
        },
      },
    },
  };

  // Calculate summary statistics
  const totalRoutes = utilizationData.length;
  const underUtilized = utilizationData.filter(util => util < 30).length;
  const overUtilized = utilizationData.filter(util => util > 80).length;

  return (
    <div className="route-utilization-container">
      {/* Summary Cards */}
      <div className="utilization-summary-cards">
        <div className="summary-card total">
          <div className="card-icon">🚌</div>
          <div className="card-content">
            <div className="card-number">{totalRoutes}</div>
            <div className="card-label">Total Routes</div>
          </div>
        </div>
        <div className="summary-card under-utilized">
          <div className="card-icon">📉</div>
          <div className="card-content">
            <div className="card-number">{underUtilized}</div>
            <div className="card-label">Under Utilized</div>
          </div>
        </div>
        <div className="summary-card over-utilized">
          <div className="card-icon">⚠️</div>
          <div className="card-content">
            <div className="card-number">{overUtilized}</div>
            <div className="card-label">Over Utilized</div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="chart-container">
        <div className="chart-wrapper">
          <Bar data={data} options={options} />
        </div>
      </div>
    </div>
  );
}

export default RouteUtilizationChart;
